import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../components/ui/form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, Building2, Save, Loader2, Plus, X, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const supplierSchema = z.object({
  company_name: z.string().min(1, 'Company name is required'),
  contact_person: z.string().min(1, 'Contact person is required'),
  phone: z.string().min(1, 'Phone number is required'),
  email: z.string().email('Invalid email address'),
  address: z.string().min(1, 'Address is required'),
  sectors: z.array(z.string()).min(1, 'At least one sector is required'),
  areas_covered: z.array(z.string()).min(1, 'At least one area is required'),
  notes: z.string().optional(),
  status: z.string().min(1, 'Status is required'),
  // Banking details (optional)
  bank_account_name: z.string().optional(),
  bank_sort_code: z.string().optional(),
  bank_account_number: z.string().optional(),
  // Company registration (optional)
  vat_number: z.string().optional(),
  company_registration_number: z.string().optional(),
});

const SupplierForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetchingSupplier, setFetchingSupplier] = useState(!!id);
  const [sectors, setSectors] = useState([]);
  const [areas, setAreas] = useState([]);
  const [showAddSector, setShowAddSector] = useState(false);
  const [showAddArea, setShowAddArea] = useState(false);
  const [newSectorName, setNewSectorName] = useState('');
  const [newAreaName, setNewAreaName] = useState('');
  const [addingSector, setAddingSector] = useState(false);
  const [addingArea, setAddingArea] = useState(false);
  const [sectorPopoverOpen, setSectorPopoverOpen] = useState(false);
  const [areaPopoverOpen, setAreaPopoverOpen] = useState(false);
  const isEditing = !!id;

  const form = useForm({
    resolver: zodResolver(supplierSchema),
    defaultValues: {
      company_name: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      sectors: [],
      areas_covered: [],
      notes: '',
      status: 'onboarded',
      bank_account_name: '',
      bank_sort_code: '',
      bank_account_number: '',
      vat_number: '',
      company_registration_number: '',
    },
  });

  const selectedSectors = form.watch('sectors') || [];
  const selectedAreas = form.watch('areas_covered') || [];

  const fetchSectors = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/sectors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSectors(response.data);
    } catch (error) {
      console.error('Failed to fetch sectors:', error);
    }
  }, [token]);

  const fetchAreas = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/areas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAreas(response.data);
    } catch (error) {
      console.error('Failed to fetch areas:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchSectors();
    fetchAreas();
  }, [fetchSectors, fetchAreas]);

  useEffect(() => {
    if (id) {
      const fetchSupplier = async () => {
        try {
          const response = await axios.get(`${API}/suppliers/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const supplier = response.data;
          // Handle both old (sector) and new (sectors) format
          const supplierSectors = supplier.sectors || (supplier.sector ? [supplier.sector] : []);
          // Handle both old (location) and new (areas_covered) format
          const supplierAreas = supplier.areas_covered || (supplier.location ? [supplier.location] : []);
          form.reset({
            company_name: supplier.company_name || '',
            contact_person: supplier.contact_person || '',
            phone: supplier.phone || '',
            email: supplier.email || '',
            address: supplier.address || '',
            sectors: supplierSectors,
            areas_covered: supplierAreas,
            notes: supplier.notes || '',
            status: supplier.status || 'onboarded',
            bank_account_name: supplier.bank_account_name || '',
            bank_sort_code: supplier.bank_sort_code || '',
            bank_account_number: supplier.bank_account_number || '',
            vat_number: supplier.vat_number || '',
            company_registration_number: supplier.company_registration_number || '',
          });
        } catch (error) {
          console.error('Failed to fetch supplier:', error);
          toast.error('Failed to load supplier');
          navigate('/suppliers');
        } finally {
          setFetchingSupplier(false);
        }
      };
      fetchSupplier();
    }
  }, [id, token, form, navigate]);

  const handleAddSector = async () => {
    if (!newSectorName.trim()) {
      toast.error('Please enter a sector name');
      return;
    }

    setAddingSector(true);
    try {
      await axios.post(`${API}/sectors`, 
        { name: newSectorName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Sector added successfully');
      await fetchSectors();
      // Add the new sector to selected sectors
      const currentSectors = form.getValues('sectors') || [];
      form.setValue('sectors', [...currentSectors, newSectorName.trim()]);
      setShowAddSector(false);
      setNewSectorName('');
    } catch (error) {
      console.error('Failed to add sector:', error);
      toast.error(error.response?.data?.detail || 'Failed to add sector');
    } finally {
      setAddingSector(false);
    }
  };

  const handleAddArea = async () => {
    if (!newAreaName.trim()) {
      toast.error('Please enter an area name');
      return;
    }

    setAddingArea(true);
    try {
      await axios.post(`${API}/areas`, 
        { name: newAreaName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Area added successfully');
      await fetchAreas();
      // Add the new area to selected areas
      const currentAreas = form.getValues('areas_covered') || [];
      form.setValue('areas_covered', [...currentAreas, newAreaName.trim()]);
      setShowAddArea(false);
      setNewAreaName('');
    } catch (error) {
      console.error('Failed to add area:', error);
      toast.error(error.response?.data?.detail || 'Failed to add area');
    } finally {
      setAddingArea(false);
    }
  };

  const toggleSector = (sector) => {
    const current = form.getValues('sectors') || [];
    if (current.includes(sector)) {
      form.setValue('sectors', current.filter(s => s !== sector));
    } else {
      form.setValue('sectors', [...current, sector]);
    }
  };

  const removeSector = (sector) => {
    const current = form.getValues('sectors') || [];
    form.setValue('sectors', current.filter(s => s !== sector));
  };

  const toggleArea = (area) => {
    const current = form.getValues('areas_covered') || [];
    if (current.includes(area)) {
      form.setValue('areas_covered', current.filter(a => a !== area));
    } else {
      form.setValue('areas_covered', [...current, area]);
    }
  };

  const removeArea = (area) => {
    const current = form.getValues('areas_covered') || [];
    form.setValue('areas_covered', current.filter(a => a !== area));
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (isEditing) {
        await axios.put(`${API}/suppliers/${id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Supplier updated successfully');
      } else {
        const response = await axios.post(`${API}/suppliers`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Supplier created successfully');
        navigate(`/suppliers/${response.data.supplier_id}`);
        return;
      }
      navigate(`/suppliers/${id}`);
    } catch (error) {
      console.error('Failed to save supplier:', error);
      toast.error(isEditing ? 'Failed to update supplier' : 'Failed to create supplier');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingSupplier) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl mx-auto" data-testid="supplier-form">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading tracking-tight">
            {isEditing ? 'Edit Supplier' : 'Add New Supplier'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isEditing ? 'Update supplier information' : 'Create a new supplier profile'}
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Company Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Company Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="company_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="Acme Corp Ltd" {...field} data-testid="company-name-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="sectors"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sectors *</FormLabel>
                      <div className="flex gap-2">
                        <Popover open={sectorPopoverOpen} onOpenChange={setSectorPopoverOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              className={cn(
                                "flex-1 justify-between font-normal",
                                selectedSectors.length === 0 && "text-muted-foreground"
                              )}
                              data-testid="sectors-select"
                            >
                              {selectedSectors.length === 0 
                                ? "Select sectors..." 
                                : `${selectedSectors.length} selected`}
                              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[300px] p-0" align="start">
                            <div className="max-h-[300px] overflow-auto p-2">
                              {sectors.map((sector) => (
                                <div
                                  key={sector}
                                  className="flex items-center space-x-2 p-2 hover:bg-muted rounded cursor-pointer"
                                  onClick={() => toggleSector(sector)}
                                >
                                  <Checkbox 
                                    checked={selectedSectors.includes(sector)}
                                    onCheckedChange={() => toggleSector(sector)}
                                  />
                                  <span className="text-sm">{sector}</span>
                                </div>
                              ))}
                              {sectors.length === 0 && (
                                <p className="text-sm text-muted-foreground p-2">No sectors available</p>
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="icon"
                          onClick={() => setShowAddSector(true)}
                          data-testid="add-sector-btn"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {/* Selected sectors display */}
                      {selectedSectors.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {selectedSectors.map((sector) => (
                            <Badge key={sector} variant="secondary" className="pl-2 pr-1 py-1">
                              {sector}
                              <button
                                type="button"
                                onClick={() => removeSector(sector)}
                                className="ml-1 hover:bg-muted rounded-full p-0.5"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="areas_covered"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Area's Covered *</FormLabel>
                      <div className="flex gap-2">
                        <Popover open={areaPopoverOpen} onOpenChange={setAreaPopoverOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              className={cn(
                                "flex-1 justify-between font-normal",
                                selectedAreas.length === 0 && "text-muted-foreground"
                              )}
                              data-testid="areas-select"
                            >
                              {selectedAreas.length === 0 
                                ? "Select areas..." 
                                : `${selectedAreas.length} selected`}
                              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[300px] p-0" align="start">
                            <div className="max-h-[300px] overflow-auto p-2">
                              {areas.map((area) => (
                                <div
                                  key={area}
                                  className="flex items-center space-x-2 p-2 hover:bg-muted rounded cursor-pointer"
                                  onClick={() => toggleArea(area)}
                                >
                                  <Checkbox 
                                    checked={selectedAreas.includes(area)}
                                    onCheckedChange={() => toggleArea(area)}
                                  />
                                  <span className="text-sm">{area}</span>
                                </div>
                              ))}
                              {areas.length === 0 && (
                                <p className="text-sm text-muted-foreground p-2">No areas available</p>
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="icon"
                          onClick={() => setShowAddArea(true)}
                          data-testid="add-area-btn"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {/* Selected areas display */}
                      {selectedAreas.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {selectedAreas.map((area) => (
                            <Badge key={area} variant="secondary" className="pl-2 pr-1 py-1">
                              {area}
                              <button
                                type="button"
                                onClick={() => removeArea(area)}
                                className="ml-1 hover:bg-muted rounded-full p-0.5"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="status-select">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="onboarded">Onboarded</SelectItem>
                          <SelectItem value="under_review">Under Review</SelectItem>
                          <SelectItem value="on_hold">On Hold</SelectItem>
                          <SelectItem value="do_not_use">Do Not Use</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address *</FormLabel>
                    <FormControl>
                      <Input placeholder="123 Business Street, London, EC1A 1BB" {...field} data-testid="address-input" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="contact_person"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact Person *</FormLabel>
                    <FormControl>
                      <Input placeholder="John Smith" {...field} data-testid="contact-person-input" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="contact@company.com" {...field} data-testid="email-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone *</FormLabel>
                      <FormControl>
                        <Input placeholder="+44 20 1234 5678" {...field} data-testid="phone-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Banking Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Banking Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="bank_account_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Account Name</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="e.g., Acme Corp Ltd" 
                        {...field} 
                        data-testid="bank-account-name-input" 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="bank_sort_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sort Code</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="e.g., 12-34-56" 
                          {...field} 
                          data-testid="bank-sort-code-input" 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bank_account_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Account Number</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="e.g., 12345678" 
                          {...field} 
                          data-testid="bank-account-number-input" 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Company Registration Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Company Registration Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="vat_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>VAT Number</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="e.g., GB123456789" 
                          {...field} 
                          data-testid="vat-number-input" 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="company_registration_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Registration Number</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="e.g., 12345678" 
                          {...field} 
                          data-testid="company-reg-number-input" 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea 
                        placeholder="Add any additional notes or comments about this supplier..."
                        className="min-h-[120px]"
                        {...field}
                        data-testid="notes-textarea"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-btn">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEditing ? 'Saving...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? 'Save Changes' : 'Create Supplier'}
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>

      {/* Add Sector Dialog */}
      <Dialog open={showAddSector} onOpenChange={setShowAddSector}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Sector</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Enter sector name"
              value={newSectorName}
              onChange={(e) => setNewSectorName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddSector()}
              data-testid="new-sector-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSector(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddSector} disabled={addingSector} data-testid="save-sector-btn">
              {addingSector ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Sector'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Area Dialog */}
      <Dialog open={showAddArea} onOpenChange={setShowAddArea}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Area</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Enter area name"
              value={newAreaName}
              onChange={(e) => setNewAreaName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddArea()}
              data-testid="new-area-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddArea(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddArea} disabled={addingArea} data-testid="save-area-btn">
              {addingArea ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Area'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierForm;
